import { combineReducers, Reducer, AnyAction } from 'redux';
import { createSelector } from 'reselect';
import groupBy from 'lodash/groupBy';
import { createLoadingReducer, createListLoadingReducer } from './utils';
import * as actions from '../actions';
import { format, toDate } from '@/utils/datetime';
import { freeSpotsOf } from '@/utils/common';

export const getTimes = (state: any) => state.availableTimes.times;

export const selectNavigationDate = (state: any) => state.availableTimes.ui.navigationDate;

export const getTimesGroupedByDay = createSelector(getTimes, (times) =>
    groupBy(times.data, (time) => format(time.From, 'yyyy-MM-dd'))
);

/** A slot's start, reduced to an instant so representation cannot matter. */
export interface IntervalLike {
    From?: Date | string | null;
    To?: Date | string | null;
}

/**
 * The one comparable form of a slot start.
 *
 * Three incompatible representations of the same moment circulate in this flow:
 * naive API strings (`2024-11-22T17:30:00`), ISO strings with a zone
 * (`2024-11-22T16:30:00.000Z`) and `Date` objects. No two of them are ever
 * string-equal, so anything that compares slots must compare instants.
 *
 * `undefined` for a missing or unparseable value — never `NaN`, which would
 * compare unequal to itself and make a Map key that can never be hit.
 */
export const toInstantKey = (value?: Date | string | null): number | undefined => {
    if (value === undefined || value === null || value === '') return undefined;

    const instant = toDate(value).getTime();
    return Number.isNaN(instant) ? undefined : instant;
};

/**
 * Free spots per slot start, from a list of available times.
 *
 * Keyed on the start alone: one available-times request carries one `Duration`,
 * so within a fetched window a start identifies exactly one slot. First entry
 * wins — a repeat could only be the same slot restated.
 */
export const buildFreeSpotsByFrom = (
    times?: Array<IntervalLike & { Free?: number; FreeSpots?: number }> | null
): Map<number, number> => {
    const byFrom = new Map<number, number>();

    (times ?? []).forEach((time) => {
        const key = toInstantKey(time?.From);
        const free = freeSpotsOf(time);

        if (key === undefined || free === undefined || byFrom.has(key)) return;

        byFrom.set(key, free);
    });

    return byFrom;
};

/**
 * How many spots are free per slot start — the single answer the whole
 * new-booking flow routes on (book versus waiting queue). Memoised on the times
 * list, and handed whole to its callers because both classify N occurrences at
 * once: `lookup.get(toInstantKey(occurrence.From))`.
 *
 * A miss means "not in the fetched window", which is NOT the same as full:
 * callers must be able to tell "no space" from "no data". Generated recurring
 * occurrences inherit the seed slot's `Free`/`FreeSpots` verbatim
 * (`getRepeatedDates` spreads the seed), which is why the occurrence's own value
 * is never trusted.
 */
export const getFreeSpotsByFrom = createSelector(getTimes, (times) =>
    buildFreeSpotsByFrom(times?.data)
);

const uiInitialState = {
    navigationDate: new Date(),
};

const availableTimes = createListLoadingReducer('AVAILABLE_TIMES');
const nextFreeTime = createLoadingReducer('NEXT_FREE_TIME', (payload) =>
    payload.Times ? payload.Times[0] : null
);

const uiReducer: Reducer<any, AnyAction> = (state = uiInitialState, { type, payload }) => {
    switch (type) {
        case actions.CHANGE_BOOKING_NAVIGATION_DATE:
            return { ...state, navigationDate: payload };
        default:
            return state;
    }
};

export default combineReducers({
    times: availableTimes,
    nextFreeTime: nextFreeTime,
    ui: uiReducer,
});
